const EXHIBITION_TYPE_INDIVIDUAL = 1;

const _cloneItemData = <T>(obj: T): T => JSON.parse(JSON.stringify(obj));
const EXHIBITION_TYPE_SRP = 2;

/** Resuelve el tipo de exhibición activo (explicito en prod o fallback legacy SRP > Individual). */
export const _resolveActiveExhibitionTypeId = (prodOrItemData: any): number | null => {
  if (prodOrItemData?.exhibitionTypeId != null) return prodOrItemData.exhibitionTypeId;

  const uoe = prodOrItemData?.unitOfExhibition;
  if (uoe?.exhibitionTypeId != null) return uoe.exhibitionTypeId;

  const itemData = prodOrItemData?.itemData || prodOrItemData;
  const formats = itemData?.exhibitionFormats;
  if (!Array.isArray(formats) || formats.length === 0) return null;

  if (formats.some(f => f?.exhibitionTypeId === EXHIBITION_TYPE_SRP)) return EXHIBITION_TYPE_SRP;
  if (formats.some(f => f?.exhibitionTypeId === EXHIBITION_TYPE_INDIVIDUAL)) return EXHIBITION_TYPE_INDIVIDUAL;
  return formats[0]?.exhibitionTypeId ?? null;
};

/** Aplica dimensiones e imagen del formato elegido sobre itemData (mutación in-place). */
export const _applyExhibitionFormatToItemData = (itemData: any, exhibitionTypeId: number): any => {
  if (!itemData || exhibitionTypeId == null) return itemData;

  const formats = itemData.exhibitionFormats;
  if (!Array.isArray(formats)) return itemData;

  const format = formats.find(f => f?.exhibitionTypeId === exhibitionTypeId);
  if (!format) {
    // Fallback funcional: si se elige Individual y backend solo envió SRP,
    // reconstruir tamaño/imagen individual desde los campos base del item.
    if (exhibitionTypeId === EXHIBITION_TYPE_INDIVIDUAL) {
      itemData.size = itemData.size || {};
      const baseWidth = itemData.width ?? itemData.size.w;
      const baseHeight = itemData.height ?? itemData.size.h;
      const baseDepth = itemData.depth ?? itemData.size.d ?? baseWidth;

      if (baseWidth != null) itemData.size.w = baseWidth;
      if (baseHeight != null) itemData.size.h = baseHeight;
      if (baseDepth != null) itemData.size.d = baseDepth;

      itemData.imagePlanogramFront =
        itemData._individualImagePlanogramFront || itemData.originalImagePlanogramFront || itemData.imagePlanogramFront;
    }
    return itemData;
  }

  if (itemData.imagePlanogramFront && !itemData._individualImagePlanogramFront) {
    itemData._individualImagePlanogramFront = itemData.imagePlanogramFront;
  }

  itemData.size = itemData.size || {};
  if (format.width != null) {
    itemData.size.w = format.width;
    itemData.width = format.width;
  }
  if (format.height != null) {
    itemData.size.h = format.height;
    itemData.height = format.height;
  }
  if (format.depth != null) {
    itemData.size.d = format.depth;
    itemData.depth = format.depth;
  }
  if (itemData.size.d == null) itemData.size.d = itemData.size.w;

  if (format.imagePlanogramFront) {
    itemData.imagePlanogramFront = format.imagePlanogramFront;
  } else if (exhibitionTypeId === EXHIBITION_TYPE_SRP) {
    itemData.imagePlanogramFront = itemData._individualImagePlanogramFront || itemData.imagePlanogramFront;
  }

  return itemData;
};

/** Verifica si un formato de exhibición entra en el estante (ancho en zona + alto). */
export const _exhibitionFormatFitsOnShelfDrop = (
  itemData: any,
  exhibitionTypeId: number,
  options: { shelfHeight: number; freeWidthInZone: number; sepX?: number; fingerSpace?: number }
): boolean => {
  const prepared = _prepareProdItemDataForExhibitionDrop(itemData, exhibitionTypeId);
  const sz = prepared?.itemData?.size;
  const w = Number(sz?.w ?? prepared?.itemData?.width ?? 0);
  const h = Number(sz?.h ?? prepared?.itemData?.height ?? 0);
  const sepX = options.sepX ?? 0;
  const fingerSpace = options.fingerSpace ?? 0;

  const widthFits = w + sepX <= options.freeWidthInZone;
  const heightFits = h + fingerSpace <= options.shelfHeight;

  return widthFits && heightFits;
};

/** Prepara itemData y exhibitionTypeId al soltar un producto con formatos de exhibición. */
export const _prepareProdItemDataForExhibitionDrop = (
  itemData: any,
  exhibitionTypeId?: number | null
): { itemData: any; exhibitionTypeId?: number } => {
  const formats = itemData?.exhibitionFormats;
  if (!Array.isArray(formats) || formats.length === 0) {
    return { itemData };
  }

  let typeId = exhibitionTypeId;
  if (typeId == null && formats.length === 1) {
    typeId = formats[0]?.exhibitionTypeId;
  }
  if (typeId == null) {
    return { itemData };
  }

  const cloned = _cloneItemData(itemData);
  _applyExhibitionFormatToItemData(cloned, typeId);
  return { itemData: cloned, exhibitionTypeId: typeId };
};
